import { Form, Formik, FormikState, useFormikContext } from 'formik';
import { Plus } from 'lucide-react';

import { pluralize } from '@/react/common/string-utils';
import { Credential } from '@/react/portainer/settings/sharedCredentials/types';
import { notifySuccess } from '@/portainer/services/notifications';
import { useCustomTemplates } from '@/react/portainer/templates/custom-templates/queries/useCustomTemplates';
import { StackType } from '@/react/common/stacks/types';
import { useTalosVersionOptions } from '@/react/kubernetes/cluster/omni/queries/useTalosVersions';
import { useInstallOmniCluster } from '@/react/kubernetes/cluster/omni/queries/useInstallOmniCluster';
import { useValidateOmniCluster } from '@/react/kubernetes/cluster/omni/queries/useValidateOmniCluster';
import { Environment } from '@/react/portainer/environments/types';
import {
  buildDefaultValue as buildTunnelDefaultValue,
  PortainerTunnelAddrField,
} from '@/react/portainer/common/PortainerTunnelAddrField';
import {
  buildDefaultValue as buildApiUrlDefaultValue,
  PortainerUrlField,
} from '@/react/portainer/common/PortainerUrlField';
import {
  OmniClusterMachineSet,
  OmniMachineFormValues,
  OmniMachinePayload,
} from '@/react/kubernetes/cluster/omni/types';
import { BetaAlert } from '@/react/portainer/environments/update-schedules/common/BetaAlert';
import { useSettings } from '@/react/portainer/settings/queries';
import { isOmniCredentials } from '@/react/portainer/settings/sharedCredentials/utils';
import { useCanExit } from '@/react/hooks/useCanExit';
import { NameField } from '@/react/portainer/environments/common/NameField/NameField';
import { MetadataFieldset } from '@/react/portainer/environments/common/MetadataFieldset';

import { PortainerSelect } from '@@/form-components/PortainerSelect';
import { TextTip } from '@@/Tip/TextTip';
import { FormControl } from '@@/form-components/FormControl';
import { FormActions } from '@@/form-components/FormActions';
import { FormSection } from '@@/form-components/FormSection';
import { WebEditorForm } from '@@/WebEditorForm';
import { confirmGenericDiscard } from '@@/modals/confirm';

import { CredentialsField } from '../../shared/CredentialsField';
import { useSetAvailableOptionFormik } from '../../shared/useSetAvailableOptionFormik';
import { CustomTemplateSelector } from '../../shared/CustomTemplateSelector';
import { AnalyticsStateKey } from '../../types';

import { CreateOmniClusterPayload, OmniCreateClusterFormValues } from './types';
import { isTalosMajorMinorExact, useOmniValidation } from './useOmniValidation';
import { getMajorAndMinorString } from './utils';
import { MachineSelectInput } from './MachineSelectInput';

const exampleClusterConfig = `# cluster:
#   network:
#     cni:
#       flannel:
#         extraArgs:
#           - --iface-can-reach=\${GATEWAY_IP}
#       name: flannel
# machine:
#   network:
#     kubespan:
#       enabled: true
`;

const initialMachineSets: OmniClusterMachineSet[] = [
  {
    name: 'Control Plane',
    role: 'control-plane',
    machines: [],
  },
  {
    name: 'Main worker pool',
    role: 'worker',
    machines: [],
  },
];

interface Props {
  onCreate(environment: Environment, analytics: AnalyticsStateKey): void;
  credentials: Credential[];
}

export function OmniCreateClusterForm({ onCreate, credentials }: Props) {
  const validateOmniClusterQuery = useValidateOmniCluster();
  const installOmniClusterQuery = useInstallOmniCluster();
  const initialValues = useInitialValues(credentials);
  return (
    <Formik
      initialValues={initialValues}
      onSubmit={handleSubmit}
      validationSchema={useOmniValidation()}
      validateOnMount
      enableReinitialize
    >
      <OmniCreateClusterInnerForm credentials={credentials} />
    </Formik>
  );

  async function handleSubmit(
    values: OmniCreateClusterFormValues,
    {
      resetForm,
    }: {
      resetForm: (
        nextState?: Partial<FormikState<OmniCreateClusterFormValues>>
      ) => void;
    }
  ) {
    const payload: CreateOmniClusterPayload = getPayloadFromFormValues(values);

    // Run an Omni dry-run preflight before the real provisioning call so that errors
    // like a Talos/Kubernetes version mismatch or a duplicate cluster name surface to
    // the user without leaving partially-created resources behind in Omni.
    try {
      await validateOmniClusterQuery.mutateAsync({
        payload,
        credentialID: values.credentialId,
      });
    } catch {
      return;
    }

    // use mutateAsync so that isSubmitting is true while the request is in progress
    await installOmniClusterQuery.mutateAsync(
      { payload, credentialId: values.credentialId },
      {
        onSuccess: (environment) => {
          notifySuccess('Success', 'Cluster provisioning started');
          onCreate(environment, 'kubernetesInstallOmni');
          resetForm();
        },
      }
    );
  }
}

function getPayloadFromFormValues(
  values: OmniCreateClusterFormValues
): CreateOmniClusterPayload {
  return {
    portainerUrl: values.omni.portainerUrl,
    tunnelServerAddr: values.omni.tunnelServerAddr,
    cluster: {
      kind: 'Cluster',
      name: values.name,
      kubernetes: {
        version: values.omni.kubernetesVersion,
      },
      talos: {
        version: values.omni.talosVersion,
      },
    },
    controlPlane: {
      kind: 'ControlPlane',
      machines: values.omni.machineSets
        .filter((set) => set.role === 'control-plane')
        .flatMap((set) =>
          set.machines.map(transformMachineValuesToMachinePayload)
        ),
    },
    worker: {
      kind: 'Workers',
      machines: values.omni.machineSets
        .filter((set) => set.role === 'worker')
        .flatMap((set) =>
          set.machines.map(transformMachineValuesToMachinePayload)
        ),
    },
    clusterConfig:
      values.omni.clusterConfig === exampleClusterConfig ||
      !values.omni.clusterConfig
        ? ''
        : values.omni.clusterConfig,
  };
}

export function transformMachineValuesToMachinePayload(
  machineValues: OmniMachineFormValues
): OmniMachinePayload {
  const payload: OmniMachinePayload = {
    name: machineValues.name,
  };

  if (machineValues.disk) {
    payload.install = { disk: machineValues.disk };
  }

  if (machineValues.systemDiskSize) {
    payload.systemDiskSize = machineValues.systemDiskSize;
  }

  if (machineValues.enableUserDisk && machineValues.userDiskVolumeName) {
    payload.userDisk = {
      volumeName: machineValues.userDiskVolumeName,
      size: machineValues.userDiskSize,
    };
  }

  if (!machineValues.applyConfig) {
    return payload;
  }

  return {
    ...payload,
    hostname: machineValues.hostname,
    interfaces: machineValues.interfaces,
    nameservers: machineValues.nameservers,
  };
}

function useInitialValues(
  credentials: Credential[]
): OmniCreateClusterFormValues {
  const credentialId = credentials[0]?.id ?? 0;
  const talosVersionOptionsQuery = useTalosVersionOptions({
    credentialId,
  });

  const talosOptions = talosVersionOptionsQuery.data?.talosVersionOptions ?? [];
  const compatibleK8sOptions =
    talosVersionOptionsQuery.data?.compatibleK8sVersionOptions ?? {};

  const initialTalosVersion = talosOptions[0]?.value ?? '';
  const initialK8sVersion =
    compatibleK8sOptions[initialTalosVersion ?? '']?.[0]?.value ?? '';

  const settingsQuery = useSettings();
  const portainerUrl =
    settingsQuery.data?.EdgePortainerUrl || buildApiUrlDefaultValue();

  const tunnelServerAddr =
    settingsQuery.data?.Edge?.TunnelServerAddress || buildTunnelDefaultValue();

  return {
    name: '',
    credentialId,
    meta: {
      groupId: 1,
      tagIds: [],
    },
    omni: {
      machineSets: initialMachineSets,
      kubernetesVersion: initialK8sVersion,
      talosVersion: initialTalosVersion,
      clusterConfig: exampleClusterConfig,
      portainerUrl,
      tunnelServerAddr,
    },
  };
}

interface InnerFormProps {
  credentials: Credential[];
}

function OmniCreateClusterInnerForm({ credentials }: InnerFormProps) {
  const { values, setFieldValue, isSubmitting, isValid, errors, dirty } =
    useFormikContext<OmniCreateClusterFormValues>();

  useCanExit(() => !dirty || confirmGenericDiscard());

  const talosVersionOptionsQuery = useTalosVersionOptions({
    credentialId: values.credentialId,
  });
  const talosOptions = talosVersionOptionsQuery.data?.talosVersionOptions ?? [];
  const compatibleK8sOptions =
    talosVersionOptionsQuery.data?.compatibleK8sVersionOptions ?? {};
  const customTemplatesQuery = useCustomTemplates({
    select: (templates) =>
      templates.filter((t) => t.Type === StackType.Kubernetes),
  });
  const customTemplates = customTemplatesQuery.data ?? [];
  useSetAvailableOptionFormik(
    compatibleK8sOptions[values.omni.talosVersion],
    values.omni.kubernetesVersion,
    'omni.kubernetesVersion'
  );

  const omniCredentials = credentials.find(
    (credential) => credential.id === values.credentialId
  )?.credentials;
  const omniEndpoint = isOmniCredentials(omniCredentials)
    ? omniCredentials?.endpoint
    : '';

  return (
    <Form className="form-horizontal">
      <BetaAlert
        isHtml
        message={
          <p>
            Beta feature - so far, Omni cluster management has only been tested
            in a limited set of scenarios.
          </p>
        }
      />
      <TextTip color="blue" className="mb-2" inline={false}>
        This will allow you to create a Kubernetes cluster using Omni with your
        own existing Talos nodes, and will then deploy the Portainer agent to
        it.
      </TextTip>

      <NameField
        tooltip="Name of the cluster and environment."
        placeholder="e.g. my-cluster-name"
      />

      <FormSection title="Portainer server details">
        <PortainerUrlField fieldName="omni.portainerUrl" required />
        <PortainerTunnelAddrField fieldName="omni.tunnelServerAddr" required />
      </FormSection>

      <FormSection title="Omni cluster summary">
        <CredentialsField credentials={credentials} />

        <FormControl
          label="Talos version"
          tooltip="Talos version running on the cluster."
          inputId="omni-talosVersion"
          isLoading={talosVersionOptionsQuery.isLoading}
          errors={
            typeof errors.omni === 'string'
              ? errors.omni // message from mismatching talos version validation
              : errors.omni?.talosVersion
          }
        >
          <PortainerSelect
            value={values.omni.talosVersion}
            onChange={(value: string) => {
              setFieldValue('omni.talosVersion', value);
            }}
            data-cy="omniCreateForm-talosVersionSelect"
            options={talosOptions}
            bindToBody
          />
        </FormControl>

        <UninstalledMachinesHint
          machineSets={values.omni.machineSets}
          clusterTalosVersion={values.omni.talosVersion}
        />

        <FormControl
          label="Kubernetes version"
          tooltip={
            <>
              Kubernetes version running on the cluster.
              <br />
              <a
                target="_blank"
                href={`https://www.talos.dev/${getMajorAndMinorString(
                  values.omni.talosVersion
                )}/introduction/support-matrix/`}
                rel="noreferrer"
              >
                Learn more about Kubernetes version compatibility.
              </a>
            </>
          }
          inputId="microk8s-kubernetesVersion"
          isLoading={talosVersionOptionsQuery.isLoading}
        >
          <PortainerSelect
            value={values.omni.kubernetesVersion}
            onChange={(value: string) =>
              setFieldValue('omni.kubernetesVersion', value)
            }
            data-cy="omniCreateForm-kubernetesVersionSelect"
            options={compatibleK8sOptions[values.omni.talosVersion] ?? []}
            bindToBody
          />
        </FormControl>
      </FormSection>

      <FormSection title="Cluster machines">
        {!!omniEndpoint && (
          <div className="flex w-full">
            <TextTip color="blue" className="mb-2" inline={false}>
              To add Talos nodes,{' '}
              <a
                href={`${omniEndpoint}/machines/installation-media`}
                target="_blank"
                rel="noreferrer"
                data-cy="omniCreateForm-downloadInstallationMediaLink"
              >
                download the Omni ISO image
              </a>{' '}
              and boot your machines using this image. For more information, see
              the{' '}
              <a
                href="https://omni.siderolabs.com/tutorials/getting_started#download-installation-media"
                target="_blank"
                rel="noreferrer"
              >
                Omni documentation
              </a>
              .
            </TextTip>
          </div>
        )}
        {values.omni.machineSets.map((machineSet, index) => (
          <MachineSelectInput
            key={machineSet.name}
            machineSet={machineSet}
            index={index}
          />
        ))}
      </FormSection>

      <FormSection title="Cluster Configuration patch" isFoldable>
        <WebEditorForm
          titleContent="Edit Cluster Configuration patch"
          id="omni-cluster-config"
          onChange={(value: string) => {
            setFieldValue('omni.clusterConfig', value);
          }}
          type="yaml"
          value={values.omni.clusterConfig ?? ''}
          data-cy="omniCreateForm-clusterConfig"
        >
          <TextTip color="blue">
            Cluster Configuration Patches allow you to customize settings for
            all nodes in a cluster or specific groups (such as control plane or
            worker nodes). See the{' '}
            <a
              target="_blank"
              href={`https://www.talos.dev/${getMajorAndMinorString(
                values.omni.talosVersion
              )}/reference/configuration/`}
              rel="noreferrer"
            >
              Talos Config Reference
            </a>{' '}
            for more information.
          </TextTip>
        </WebEditorForm>
      </FormSection>

      <FormSection title="More settings" className="ml-0" isFoldable>
        <div className="ml-8">
          <CustomTemplateSelector customTemplates={customTemplates} />
          <MetadataFieldset>
            <TextTip color="blue" className="mb-2">
              Metadata is only assigned to the environment in Portainer, i.e.
              the group and tags are not assigned to the cluster within the Omni
              dashboard.
            </TextTip>
          </MetadataFieldset>
        </div>
      </FormSection>
      <FormActions
        submitLabel="Provision environment"
        loadingText="Provisioning..."
        submitIcon={Plus}
        isLoading={isSubmitting}
        data-cy="provision-omni-environment-button"
        isValid={isValid}
      />
    </Form>
  );
}

// UninstalledMachinesHint surfaces Omni's official ISO/PXE-mismatch warning
// when one or more selected machines (a) do not have Talos installed to disk
// and (b) have a major.minor that does NOT match the chosen cluster Talos
// version. When the versions already match (e.g. v1.12.x machine + v1.12.x
// cluster) there is nothing to warn about, so the hint stays hidden.
export function UninstalledMachinesHint({
  machineSets,
  clusterTalosVersion,
}: {
  machineSets: OmniClusterMachineSet[];
  clusterTalosVersion?: string;
}) {
  if (!clusterTalosVersion) {
    return null;
  }

  const mismatched = machineSets
    .flatMap((set) => set.machines)
    .filter(
      (machine) =>
        machine.installed === false &&
        !!machine.talosVersion &&
        !isTalosMajorMinorExact(machine.talosVersion, clusterTalosVersion)
    );

  if (mismatched.length === 0) {
    return null;
  }

  const machineSummary = mismatched
    .map((machine) => {
      const label = machine.hostname || machine.name;
      const version = machine.talosVersion
        ? ` (Talos ${getMajorAndMinorString(machine.talosVersion)})`
        : '';
      return `${label}${version}`;
    })
    .join(', ');

  return (
    <TextTip color="orange" inline={false} className="mb-2">
      The machine running from ISO or PXE must have the same major and minor
      version as the cluster it is going to be added to. Please use another ISO
      or change the cluster Talos version. Affected{' '}
      {pluralize(mismatched.length, 'machine')}: {machineSummary}.
    </TextTip>
  );
}
